import { CalibrationDisplaySchema, CalibrationInputFieldConfig } from "./types";
import { sensorSchemaRegistry } from "./registry";
import { getCategoryFromValue } from "./categoryUtils";
import { displacementSensorSchema } from "./displacement";

const detectSchemaFromCategory = (category?: string | null): string | null =>
  getCategoryFromValue(category);

/**
 * Last-resort schema detection from the shape of a calibration's raw
 * `input_data`, used only when neither sensor category nor sensor type
 * resolved to a registered schema (e.g. `sensor.sensor_type` wasn't
 * populated on a particular record/row). Distinguishes sensor types by
 * fields unique to each backend sensor's input schema, so a category/type
 * metadata gap doesn't silently render one sensor's data with another
 * sensor's sections/tables (e.g. a resistive strain gauge calibration
 * rendered against the displacement schema's Measurements/Analysis tables).
 */
function detectSchemaTypeFromInputDataShape(inputData: unknown): string | null {
  if (!inputData || typeof inputData !== "object") return null;
  const data = inputData as Record<string, any>;

  // Resistive strain gauge: full_scale_range + 4-channel calibration_coefficients.
  if ("full_scale_range" in data && data.calibration_coefficients?.offset_ch0 !== undefined) {
    return "resistive_strain_gauge";
  }

  // Tiltmeter: g_range + tilt-angle raw readings.
  if ("g_range" in data && (data.test_report?.tilt_readings || data.tilt_readings)) {
    return "tiltmeter";
  }

  // Accelerometer: g_range + axis-based test results, no tilt readings.
  if ("g_range" in data && (data.test_report?.test_results || data.test_results)) {
    return "accelerometer";
  }

  // Vibrating-wire style (displacement/piezometer) — category still decides
  // displacement vs piezometer for unit/labels; this is only a last-resort
  // fallback, and displacement is already this module's own final default.
  if ("raw_frequency" in data || "abc_coefficients" in data) {
    return "displacement";
  }

  return null;
}

/**
 * Determine the appropriate schema type from sensor category and type
 * Priority: category (if mapped) > sensorType > raw input_data shape > default
 * @param sensorCategory - The sensor category (e.g., "piezometer", "displacement")
 * @param sensorType - The sensor type identifier (e.g., "vibrating_wire", "VW")
 * @param inputDataHint - Optional raw `calibration.input_data`, used only as a
 *   last resort when category and type both fail to resolve
 * @returns The schema type identifier to use
 */
export function getSchemaTypeFromCategoryAndType(
  sensorCategory: string | null | undefined,
  sensorType: string | null | undefined,
  inputDataHint?: unknown
): string | null {
  // Check if category has a direct mapping
  const categorySchema = detectSchemaFromCategory(sensorCategory);
  if (categorySchema) {
    return categorySchema;
  }

  // Fall back to sensor type, but only if it actually names a registered
  // schema — some categories' underlying `sensor_type.type` value (e.g.
  // "Dynamic" for resistive strain gauge) doesn't match any registry key,
  // and blindly returning it here would skip straight past the data-shape
  // fallback below, straight to displacement.
  if (sensorType) {
    const normalizedType = sensorType.toLowerCase().replace(/[\s-]/g, "_");
    if (normalizedType in sensorSchemaRegistry) {
      return sensorType;
    }
  }

  // Category didn't map, and sensorType (if present) isn't a recognized
  // schema key — sniff the actual submitted payload shape rather than
  // silently defaulting.
  const detectedFromShape = detectSchemaTypeFromInputDataShape(inputDataHint);
  if (detectedFromShape) {
    return detectedFromShape;
  }

  return sensorType || null;
}

/**
 * Get schema for a sensor type
 * @param sensorType - The sensor type identifier (e.g., "vibrating_wire", "piezometer", "VW")
 * @returns The calibration display schema or vibrating wire schema as default
 */
export function getSensorSchema(sensorType: string | null): CalibrationDisplaySchema {
  if (!sensorType) {
    return displacementSensorSchema; // Default schema
  }

  // Normalize sensor type (handle various formats)
  const normalizedType = sensorType.toLowerCase().replace(/[\s-]/g, "_");
  
  return sensorSchemaRegistry[normalizedType] || displacementSensorSchema;
}

// Re-export registry for convenience
export { sensorSchemaRegistry } from "./registry";

/**
 * Get schema for a sensor using category and type
 * This is the recommended method as it handles category priority automatically
 * @param sensorCategory - The sensor category (e.g., "piezometer", "displacement")
 * @param sensorType - The sensor type identifier (e.g., "vibrating_wire", "VW")
 * @returns The calibration display schema or vibrating wire schema as default
 */
export function getSensorSchemaFromCategoryAndType(
  sensorCategory: string | null | undefined,
  sensorType: string | null | undefined,
  inputDataHint?: unknown
): CalibrationDisplaySchema {
  const schemaType = getSchemaTypeFromCategoryAndType(sensorCategory, sensorType, inputDataHint);
  return getSensorSchema(schemaType);
}

/**
 * Get calibration field configuration from sensor schema
 * Uses category-aware schema selection to get sensor-specific configuration
 * @param sensorType - The sensor type identifier
 * @param category - The sensor category (e.g., "piezometer", "displacement")
 * @returns The calibration input field configuration, or null if not found
 */
export function getCalibrationFieldConfig(
  sensorType: string | null,
  category?: string
): CalibrationInputFieldConfig | null {
  // Use category-aware schema selection to get the correct sensor schema
  const schema = getSensorSchemaFromCategoryAndType(category, sensorType);
  
  // Return the sensor-specific configuration from the schema
  // Each sensor schema should define its own inputFieldConfig
  return schema.inputFieldConfig || null;
}
